import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  InputAdornment,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import DoneAllIcon from '@mui/icons-material/DoneAll';
import { useState } from 'react';
import { useAuth } from '../components/AuthGate';
import { Notification, NotificationStatus, NotificationsApi, PaginatedNotifications } from '../api/notifications';

const LIMIT = 20;

function timeAgo(iso: string) {
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60_000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}

export default function BrowsePage() {
  const { token } = useAuth();

  // Filter state
  const [recipientId, setRecipientId] = useState('');
  const [status, setStatus] = useState<NotificationStatus | ''>('');

  // Results state
  const [result, setResult] = useState<PaginatedNotifications | null>(null);
  const [offset, setOffset] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [markingId, setMarkingId] = useState<string | null>(null);

  const fetchPage = async (pageOffset: number) => {
    if (!recipientId.trim()) {
      setError('Recipient ID is required.');
      return;
    }
    setError('');
    setLoading(true);
    try {
      const data = await NotificationsApi.list(
        {
          recipientId: recipientId.trim(),
          status: status || undefined,
          limit: LIMIT,
          offset: pageOffset,
        },
        token,
      );
      setResult(data);
      setOffset(pageOffset);
    } catch (e: unknown) {
      const msg =
        e && typeof e === 'object' && 'response' in e
          ? ((e as { response?: { data?: { message?: string } } }).response?.data?.message ?? 'Request failed')
          : 'Request failed';
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    fetchPage(0);
  };

  const handleMarkAsRead = async (n: Notification) => {
    if (n.status === 'read') return;
    setMarkingId(n.id);
    try {
      const updated = await NotificationsApi.markAsRead(n.id, token);
      setResult((prev) =>
        prev
          ? {
              ...prev,
              data: prev.data.map((item) => (item.id === updated.id ? updated : item)),
            }
          : prev,
      );
    } catch {
      // ignore
    } finally {
      setMarkingId(null);
    }
  };

  return (
    <Box>
      {/* Filter form */}
      <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
        <form onSubmit={handleSearch}>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'flex-end' }}>
            <TextField
              label="Recipient ID"
              size="small"
              required
              value={recipientId}
              onChange={(e) => setRecipientId(e.target.value)}
              sx={{ minWidth: 200 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />

            <Box sx={{ minWidth: 120 }}>
              <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>
                Status
              </Typography>
              <Select
                size="small"
                fullWidth
                value={status}
                onChange={(e) => setStatus(e.target.value as NotificationStatus | '')}
                displayEmpty
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="unread">Unread</MenuItem>
                <MenuItem value="read">Read</MenuItem>
              </Select>
            </Box>

            <Button
              type="submit"
              variant="contained"
              disabled={loading || !recipientId.trim()}
              startIcon={loading ? <CircularProgress size={14} /> : <SearchIcon fontSize="small" />}
            >
              Search
            </Button>
          </Box>
        </form>
      </Paper>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {/* Results */}
      {result && (
        <>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
            <Typography variant="body2" color="text.secondary">
              {result.pagination.count} result{result.pagination.count !== 1 ? 's' : ''}
            </Typography>
          </Box>

          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: 'grey.50' }}>
                  <TableCell sx={{ fontWeight: 600, fontSize: 12 }}>Title / Message</TableCell>
                  <TableCell sx={{ fontWeight: 600, fontSize: 12 }}>Recipient</TableCell>
                  <TableCell sx={{ fontWeight: 600, fontSize: 12 }}>Status</TableCell>
                  <TableCell sx={{ fontWeight: 600, fontSize: 12 }}>Created</TableCell>
                  <TableCell sx={{ fontWeight: 600, fontSize: 12 }} />
                </TableRow>
              </TableHead>
              <TableBody>
                {result.data.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                      No notifications found
                    </TableCell>
                  </TableRow>
                ) : (
                  result.data.map((n) => (
                    <TableRow
                      key={n.id}
                      sx={{
                        bgcolor: n.status === 'unread' ? 'action.hover' : 'transparent',
                        '&:hover': { bgcolor: 'action.selected' },
                      }}
                    >
                      <TableCell sx={{ maxWidth: 260 }}>
                        <Typography
                          variant="body2"
                          fontWeight={n.status === 'unread' ? 600 : 400}
                          noWrap
                        >
                          {n.title}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" display="block" noWrap>
                          {n.message}
                        </Typography>
                      </TableCell>

                      <TableCell>
                        <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>
                          {n.recipientId}
                        </Typography>
                      </TableCell>

                      <TableCell>
                        <Chip
                          label={n.status}
                          size="small"
                          color={n.status === 'unread' ? 'warning' : 'default'}
                          sx={{ fontSize: 10, height: 18 }}
                        />
                      </TableCell>

                      <TableCell>
                        <Tooltip title={new Date(n.createdAt).toLocaleString()}>
                          <Typography variant="caption" color="text.secondary">
                            {timeAgo(n.createdAt)}
                          </Typography>
                        </Tooltip>
                      </TableCell>

                      <TableCell align="right">
                        {n.status === 'unread' && (
                          <Tooltip title="Mark as read">
                            <span>
                              <Button
                                size="small"
                                onClick={() => handleMarkAsRead(n)}
                                disabled={markingId === n.id}
                                sx={{ minWidth: 0, p: 0.5 }}
                              >
                                {markingId === n.id ? (
                                  <CircularProgress size={14} />
                                ) : (
                                  <DoneAllIcon fontSize="small" />
                                )}
                              </Button>
                            </span>
                          </Tooltip>
                        )}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Pagination */}
          {result.pagination.count > LIMIT && (
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2 }}>
              <Typography variant="caption" color="text.secondary">
                {offset + 1}–{Math.min(offset + LIMIT, result.pagination.count)} of{' '}
                {result.pagination.count}
              </Typography>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button
                  size="small"
                  disabled={offset === 0}
                  onClick={() => fetchPage(offset - LIMIT)}
                  sx={{ textTransform: 'none' }}
                >
                  Previous
                </Button>
                <Button
                  size="small"
                  disabled={!result.pagination.hasMore}
                  onClick={() => fetchPage(offset + LIMIT)}
                  sx={{ textTransform: 'none' }}
                >
                  Next
                </Button>
              </Box>
            </Box>
          )}

          <Divider sx={{ mt: 3 }} />
          <Typography variant="caption" color="text.secondary" display="block" mt={1}>
            ID prefix shown for reference · Click the ✓ icon on any unread row to mark it as read
          </Typography>
        </>
      )}
    </Box>
  );
}
